import { ChevronsLeft } from 'lucide-react'
import { NavLink } from 'react-router-dom'
import type { NavItem } from '../../config/navigation'

interface SectionSidebarProps {
  section: NavItem
  collapsed: boolean
  onToggle: () => void
  projectId: string
}

function getBadgeClass(badge: string): string {
  const lower = badge.toLowerCase()
  if (lower === 'beta') {
    return 'bg-orion-primary-soft text-orion-primary'
  }
  if (lower === 'new') {
    return 'bg-green-100 text-green-700'
  }
  // "Bientot", "soon", "V2" and anything else
  return 'bg-light-border text-[#555A73]'
}

export default function SectionSidebar({
  section,
  collapsed,
  onToggle,
  projectId,
}: SectionSidebarProps) {
  if (collapsed) return null

  return (
    <aside
      className="w-[220px] min-w-[220px] bg-white border-r border-light-border overflow-y-auto sticky top-[56px] pt-4"
      style={{ height: 'calc(100vh - 56px)' }}
    >
      {/* Header */}
      <div className="flex flex-row items-center justify-between px-4 mb-2">
        <span className="text-sm font-semibold text-[#1B2559] truncate">
          {section.label}
        </span>
        <button
          onClick={onToggle}
          className="text-[#8B8FA7] hover:text-[#1B2559] cursor-pointer transition-colors shrink-0"
          aria-label="Réduire le panneau"
        >
          <ChevronsLeft size={16} />
        </button>
      </div>

      {/* Groups */}
      {section.groups?.map((group, groupIndex) => (
        <div key={groupIndex}>
          {/* Group header */}
          {group.name && (
            <p className="text-[11px] uppercase tracking-wider text-[#8B8FA7] font-medium px-3 mt-4 mb-1">
              {group.name}
            </p>
          )}

          {/* Items */}
          {group.items.map((child) => {
            // Hors contexte projet (pages globales : /themes, /settings, /costs),
            // projectId est absent → on renvoie vers le sélecteur de projet plutôt
            // que de construire un lien cassé `/p//...` (projectId vide → 500 API).
            const to = projectId ? `/p/${projectId}/${child.path}` : '/projects'

            if (child.disabled) {
              return (
                <div
                  key={child.id}
                  className="flex items-center justify-between text-[13px] py-1.5 px-3 mx-2 rounded-md text-[#555A73] opacity-40 pointer-events-none"
                >
                  <span>{child.label}</span>
                  {child.badge && (
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ml-2 shrink-0 ${getBadgeClass(child.badge)}`}
                    >
                      {child.badge}
                    </span>
                  )}
                </div>
              )
            }

            return (
              <NavLink
                key={child.id}
                to={to}
                className={({ isActive }) =>
                  [
                    'flex items-center justify-between text-[13px] py-1.5 px-3 mx-2 rounded-md transition-colors',
                    isActive
                      ? 'bg-orion-primary-soft text-orion-primary font-medium'
                      : 'text-[#555A73] hover:bg-light-surface-hover',
                  ].join(' ')
                }
              >
                <span className="truncate">{child.label}</span>
                {child.badge && (
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ml-2 shrink-0 ${getBadgeClass(child.badge)}`}
                  >
                    {child.badge}
                  </span>
                )}
              </NavLink>
            )
          })}
        </div>
      ))}
    </aside>
  )
}
